import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash-es';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { FirstContentEntity } from 'client/data/models/editorial-review';
import { buildMmysMsrpPricesPath, VehicleModel } from 'client/data/models/vehicle-v2';
import { MENU_HASH } from 'site-modules/shared/constants/core-page/menu-hash';
import { SCROLL_OFFSET } from 'site-modules/shared/constants/sub-navigation';
import { getParamsFromVehicle } from 'site-modules/shared/utils/core-page/params';
import { getPriceRangeString } from 'site-modules/shared/utils/price-utils';
import { PopoverItem } from 'site-modules/shared/components/popover-item/popover-item';
import { ScrollLink } from 'site-modules/shared/components/scroll-link/scroll-link';

import './vehicle-info.scss';

const CREATIVE_ID = 'msrp_range';

function VehicleInfoUI({
  className = null,
  priceRangeClassName = 'pt-0_5 pb-md-0_5 pe-md-1',
  msrpPriceRange = null,
  priceClassName = 'd-inline-block size-20',
  containerTag: ContainerTag = 'div',
  wrapperTag: WrapperTag = 'div',
  priceTag: PriceTag = 'strong',
  firstContent = null,
  isPreprod = false,
  isMobile = false,
}) {
  const minPrice = get(msrpPriceRange, `[${get(msrpPriceRange, 'length', 0) - 1}]`);
  const maxPrice = get(msrpPriceRange, '[0]');
  const hasPrice = !!(minPrice && maxPrice);
  const vehiclePrice = hasPrice ? getPriceRangeString(minPrice, maxPrice) : '$';

  const availableDate = !!firstContent && get(firstContent, 'releaseDate.value');
  const estimatedPrice = !!firstContent && get(firstContent, 'price.value');

  return (
    <ContainerTag className={classnames('intro-vehicle-info', className)}>
      {isPreprod && availableDate && (
        <WrapperTag className="pe-md-1">
          Release Date:&nbsp;
          <strong className="d-inline-block size-20">{availableDate}</strong>
        </WrapperTag>
      )}
      {hasPrice && (
        <WrapperTag className={priceRangeClassName} data-tracking-parent={CREATIVE_ID}>
          {isMobile ? 'Price:' : 'Price Range:'}&nbsp;
          <PriceTag className={priceClassName}>
            {isMobile ? (
              <ScrollLink
                className="price-link d-inline-block"
                to={MENU_HASH.TRIM_SUMMARY_MOBILE}
                scrollConfig={{ offset: SCROLL_OFFSET }}
                data-tracking-id="view_pricing_content"
                data-tracking-value="MSRP Range"
              >
                {vehiclePrice}
              </ScrollLink>
            ) : (
              vehiclePrice
            )}
            <PopoverItem
              className="ms-0_25"
              iconClassName="medium"
              openTrackingParams={{ creative_id: CREATIVE_ID, value: 'MSRP Range' }}
              screenReaderTooltipLabel="Price range"
            >
              Price range reflects Base MSRP for various trim levels, not including options or fees.
            </PopoverItem>
          </PriceTag>
        </WrapperTag>
      )}
      {!hasPrice && !!estimatedPrice && (
        <WrapperTag className={priceRangeClassName}>
          Estimated Price:&nbsp;
          <PriceTag className={priceClassName}>{estimatedPrice}</PriceTag>
        </WrapperTag>
      )}
    </ContainerTag>
  );
}

VehicleInfoUI.propTypes = {
  msrpPriceRange: PropTypes.arrayOf(PropTypes.number),
  firstContent: FirstContentEntity,
  className: PropTypes.string,
  priceRangeClassName: PropTypes.string,
  priceClassName: PropTypes.string,
  containerTag: PropTypes.string,
  priceTag: PropTypes.string,
  wrapperTag: PropTypes.string,
  isPreprod: PropTypes.bool,
  isMobile: PropTypes.bool,
};

export const VehicleInfo = connectToModel(VehicleInfoUI, {
  msrpPriceRange: bindToPath(({ vehicle }) => buildMmysMsrpPricesPath(getParamsFromVehicle(vehicle)), VehicleModel),
});
